import { SendBox } from "@azure/communication-react";
import { useState } from "react";
import {
	CCAttachmentMetadataInProgress,
	MediaTypeLimits,
	MimeToWhatsAppTypeMap,
	WhatsAppMessageType,
} from "./types";
import { useIntl } from "react-intl";
import { generateUploadUrls } from "@/repository/queryFns";
import {
	deleteFileFromBlobStorage,
	uploadFileToBlobStorage,
} from "@/repository/queryFns/blobStorage";
import { MessagingComposerShell } from "./MessagingComposerShell";

interface TextEditorProps {
	sendMessages: (
		content: string,
		attachments?: CCAttachmentMetadataInProgress[]
	) => void;
	allowAttachments: boolean;
	interactionId: string;
	isDisabled?: boolean;
}

export const WebchatTextEditor: React.FC<TextEditorProps> = ({
	sendMessages,
	allowAttachments = true,
	interactionId,
	isDisabled,
}) => {
	const [attachments, setAttachments] = useState<
		CCAttachmentMetadataInProgress[]
	>([]);

	const intl = useIntl();
	const [systemMessage, setSystemMessage] = useState("");

	const displayErrorMessage = (message: string) => {
		setSystemMessage(message);
		setTimeout(() => {
			setSystemMessage("");
		}, 5000);
	};

	const validateFiles = (files: File[]) => {
		if (!files || files.length === 0) {
			return { isValid: false, message: "" };
		}

		const errors: string[] = [];

		if (files.length + attachments.length > 5) {
			errors.push(
				intl.formatMessage({
					id: "Interaction.WhatsAppContent.MaxFilesUpload",
				})
			);
		}

		const newFiles = Array.from(files);
		const fileNames = attachments.map((a) => a.name);

		const duplicateFiles = newFiles.filter((file) =>
			fileNames.some((name) => name === file.name)
		);

		if (duplicateFiles.length > 0) {
			errors.push(
				intl.formatMessage({
					id: "Interaction.WhatsAppContent.AlreadyAttached",
				})
			);
		}

		newFiles.forEach((file) => {
			let maxSize = MediaTypeLimits[file.type];

			//.amr extension is not correctly detected
			if (file.name.endsWith(".amr")) {
				maxSize = MediaTypeLimits["audio/amr"];
			}

			if (!maxSize) {
				errors.push(
					intl.formatMessage(
						{
							id: "Interaction.WhatsAppContent.FileExtensionNotSupported",
						},
						{
							filename: file.name,
						}
					)
				);
			} else if (file.size > maxSize) {
				errors.push(
					intl.formatMessage(
						{
							id: "Interaction.WhatsAppContent.FileSizeTooLarge",
						},
						{
							filename: file.name,
							size: (maxSize / (1024 * 1024)).toFixed(2),
						}
					)
				);
			}
		});

		if (errors.length > 0) {
			return { isValid: false, message: errors.join(" ") };
		}

		return { isValid: true, message: "" };
	};

	const uploadAttachmentsToStorage = async (
		newAttachments: CCAttachmentMetadataInProgress[]
	) => {
		await Promise.all(
			newAttachments.map(async (element) => {
				if (element.uploadUrl) {
					try {
						const uploadedUrl = await uploadFileToBlobStorage(
							element.file,
							element.uploadUrl
						);

						setAttachments((prevAttachments) =>
							prevAttachments.map((prevAttachment) =>
								prevAttachment.id === element.id
									? { ...prevAttachment, progress: 1, url: uploadedUrl }
									: prevAttachment
							)
						);
					} catch (error: any) {
						setAttachments((prev) =>
							prev.filter((item) => item.id !== element.id)
						);
						displayErrorMessage(
							intl.formatMessage(
								{
									id: "Interaction.TextEditor.ErrorOnUploadForFile",
								},
								{ filename: element.name }
							)
						);
					}
				} else {
					setAttachments((prev) =>
						prev.filter((item) => item.id !== element.id)
					);
					displayErrorMessage(
						intl.formatMessage(
							{
								id: "Interaction.TextEditor.ErrorWhileGettingUploadUrlForFile",
							},
							{ filename: element.name }
						)
					);
				}
			})
		);
	};

	const processFiles = async (files: File[]) => {
		setSystemMessage("");

		const validationResult = validateFiles(files);

		if (!validationResult.isValid) {
			displayErrorMessage(validationResult.message);

			return;
		}

		let newAttachments = files.map((file) => {
			let type =
				MimeToWhatsAppTypeMap[file.type] ?? WhatsAppMessageType.Unknown;

			//.amr extension is not correctly detected
			if (file.name.endsWith(".amr")) {
				type = WhatsAppMessageType.Audio;
			}

			return {
				id: crypto.randomUUID(),
				name: file.name,
				url: URL.createObjectURL(file),
				progress: 0.2,
				type: type,
				file: file,
			} as CCAttachmentMetadataInProgress;
		});

		const uploadUrlRequests = newAttachments.map((x) => ({
			id: x.id,
			name: x.name,
		}));

		const uploadUrlResponses = await generateUploadUrls(
			uploadUrlRequests,
			interactionId
		);

		newAttachments = newAttachments.map((newAtt) => ({
			...newAtt,
			uploadUrl: uploadUrlResponses.find((x) => x.id == newAtt.id)?.url || "",
		}));

		setAttachments((prevAttachments) => [
			...prevAttachments,
			...newAttachments,
		]);

		await uploadAttachmentsToStorage(newAttachments);
	};

	const handleTyping = async () => {
		if (systemMessage !== "") {
			setSystemMessage("");
		}
	};

	return (
		<MessagingComposerShell
			processFiles={processFiles}
			allowAttachments={allowAttachments}
			isDisabled={isDisabled}
		>
			<SendBox
				supportNewline
				attachments={attachments}
				onTyping={handleTyping}
				onSendMessage={async (content: string) => {
					sendMessages(content, attachments);
					setAttachments([]);
				}}
				onCancelAttachmentUpload={async (attachmentId: string) => {
					const url = attachments.find((x) => x.id == attachmentId)?.url;
					if (url) {
						await deleteFileFromBlobStorage(url);
					}

					setAttachments(attachments.filter((a) => a.id !== attachmentId));
				}}
				systemMessage={systemMessage}
				disabled={isDisabled}
				strings={{
					placeholderText: intl.formatMessage({
						id: "Interaction.TextEditor.TypeAMessage",
					}),
					attachmentUploadsPendingError: intl.formatMessage({
						id: "Interaction.TextEditor.UploadIncomplete",
					}),
				}}
				styles={{
					systemMessage: {
						whiteSpace: "pre-line",
					},
				}}
			/>
		</MessagingComposerShell>
	);
};
